import { useEffect, useState } from "react";
import { Mic, Square, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMicLevels } from "@/hooks/use-mic-levels";
import { toneClasses } from "@/components/ui/tone";
import { cn } from "@/lib/utils";

/* The circular face the capture acts wear: an icon, room to breathe, and the
   press/hover/focus affordances a control that starts a microphone deserves. */
const ICON_BUTTON =
  "inline-flex size-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-all hover:bg-accent hover:shadow active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/* Matches what useAudioRecorder can produce plus the WAV/OGG takes a phone or
   a transcription service hands back, so a clip recorded elsewhere is not
   refused by the picker that exists to take it. */
const AUDIO_ACCEPT =
  "audio/webm,audio/ogg,audio/mp4,audio/wav,.webm,.ogg,.m4a,.wav";

const WAVEFORM_BARS = 12;

function clock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes)}:${String(rest).padStart(2, "0")}`;
}

/**
 * The elapsed clock. Mounted only while recording, so it starts at 0:00 by
 * construction and needs no reset.
 */
function Timer({ idPrefix }: { idPrefix: string }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed((held) => held + 1);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return (
    <span
      data-testid={`${idPrefix}-timer`}
      className="text-[11px] font-semibold tabular-nums text-destructive"
    >
      {clock(elapsed)}
    </span>
  );
}

/** The strip a running capture wears: dot, waveform, clock, stop. */
function RecordingStrip({
  idPrefix,
  stream,
  onStop,
}: {
  idPrefix: string;
  stream: MediaStream | null;
  onStop: () => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-none items-center gap-2.5 rounded-full border py-1 pl-3 pr-1",
        toneClasses("destructive", "surface"),
      )}
    >
      <span
        aria-hidden
        className="size-2 flex-none animate-pulse-ring rounded-full bg-destructive"
      />
      <Waveform idPrefix={idPrefix} stream={stream} />
      <Timer idPrefix={idPrefix} />
      <span aria-live="polite" className="sr-only">
        Recording
      </span>
      <button
        type="button"
        data-testid={`${idPrefix}-record`}
        aria-label="Stop recording"
        title="Stop recording"
        className={cn(
          ICON_BUTTON,
          "size-7 border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90",
        )}
        onClick={onStop}
      >
        <Square className="size-3 fill-current" aria-hidden />
      </button>
    </div>
  );
}

/**
 * The moving trace that tells the speaker their voice is landing.
 *
 * Live bars when the browser lets the meter listen; a gentle breathing
 * animation where it cannot, so the strip never sits dead over a working
 * recording.
 */
function Waveform({
  idPrefix,
  stream,
}: {
  idPrefix: string;
  stream: MediaStream | null;
}) {
  const levels = useMicLevels(stream, WAVEFORM_BARS);
  return (
    <span
      data-testid={`${idPrefix}-waveform`}
      aria-hidden
      className="flex h-6 items-center gap-[2px]"
    >
      {Array.from({ length: WAVEFORM_BARS }, (_, bar) => (
        <span
          key={bar}
          className={cn(
            "w-[3px] rounded-full bg-destructive/80",
            levels === null && "origin-center animate-waveform-idle",
          )}
          style={
            levels === null
              ? {
                  // Every bar its own height, pace, and phase, so the idle
                  // strip reads as a live thing rather than a metronome.
                  height: `${String(7 + ((bar * 5) % 4) * 4)}px`,
                  animationDuration: `${String(850 + ((bar * 7) % 5) * 130)}ms`,
                  animationDelay: `${String(((bar * 11) % 6) * 70)}ms`,
                }
              : {
                  height: `${String(4 + (levels[bar] ?? 0) * 20)}px`,
                  transition: "height 80ms ease-out",
                }
          }
        />
      ))}
    </span>
  );
}

/**
 * The capture pair: record from the microphone, or pick an existing clip.
 *
 * Presentation only; the caller owns the recorder and hands the live state
 * in. At rest it is two circular icon buttons. While recording it becomes a
 * strip — pulsing dot, waveform, elapsed clock, stop — and the picker goes
 * away, because the take being captured is the one being given.
 */
export function AudioCaptureControls({
  recording,
  stream,
  idPrefix,
  onStart,
  onStop,
  onUpload,
  accept = AUDIO_ACCEPT,
}: {
  recording: boolean;
  /** The live microphone stream while recording, for the level meter. */
  stream: MediaStream | null;
  /** Names this instance's test ids: `${idPrefix}-record` and `${idPrefix}-upload`. */
  idPrefix: string;
  onStart: () => void;
  onStop: () => void;
  onUpload: (file: File) => void;
  accept?: string;
}) {
  if (recording) {
    return (
      <RecordingStrip idPrefix={idPrefix} stream={stream} onStop={onStop} />
    );
  }

  return (
    <div className="flex flex-none items-center gap-2">
      {/* Named in words, as the design labels it: the one way to talk the figure through. */}
      <Button
        type="button"
        variant="secondary"
        size="dense"
        data-testid={`${idPrefix}-record`}
        onClick={onStart}
      >
        <Mic aria-hidden />
        Record
      </Button>
      {/* A label rather than a button, so the press opens the picker itself. */}
      <label
        aria-label="Upload a clip"
        title="Upload a clip"
        className={cn(ICON_BUTTON, "cursor-pointer")}
      >
        <Upload className="size-4" aria-hidden />
        <input
          data-testid={`${idPrefix}-upload`}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) onUpload(file);
            event.target.value = "";
          }}
        />
      </label>
    </div>
  );
}
